<template>
  <div class="general-indicators">
    <div class="general-indicators__header">
      <h2 class="general-indicators__title">Общие показатели отчёта</h2>
      <v-menu
        v-if="canViewGeneralIndicators"
        bottom
        left
        offset-y
        origin="right right"
        transition="scale-transition"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            color="primary"
            outlined
            class="text-none general-indicators__btn"
            dark
            rounded
            v-bind="attrs"
            v-on="on"
          >
            <v-icon left>mdi-file-chart-outline</v-icon>
            Скачать отчёты
          </v-btn>
        </template>
        <v-list width="auto" dense>
          <v-list-item class="list-item-mobile-version">
            <v-btn
              @click.prevent="downloadReportIndicatorsDoorphone()"
              color="primary"
              outlined
              class="text-none mr-2"
              :class="{ 'mb-2': tableCellSmall }"
              dark
              rounded
            >
              Отчёт агентов
            </v-btn>
            <v-btn
              @click.prevent="downloadReportCompanies()"
              color="primary"
              outlined
              class="text-none mr-2"
              :class="{ 'mb-2': tableCellSmall }"
              dark
              rounded
            >
              Отчёт компаний
            </v-btn>
            <v-btn
              @click.prevent="downloadOfflineDevicesReport()"
              color="primary"
              outlined
              class="text-none mr-2"
              dark
              rounded
            >
              Устр. не в сети
            </v-btn>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div
      class="statistics-actual-info"
      v-if="canViewAbonentsStatistics || canViewMtoIndicators"
    >
      <span class="statistics-actual-text">Актуально на данный момент:</span>
      <span class="statistics-actual-date">{{ currentDateTime }}</span>
    </div>

    <div
      v-if="visibleSections.length > 1"
      class="general-indicators__sections-wrap"
    >
      <v-tabs
        v-model="activeSection"
        class="general-indicators__sections"
        show-arrows
        grow
      >
        <v-tab
          v-for="section in visibleSections"
          :key="section.key"
          class="general-indicators__section-tab"
        >
          {{ section.title }}
        </v-tab>
      </v-tabs>
    </div>

    <v-tabs-items
      v-if="visibleSections.length > 1"
      v-model="activeSection"
      class="general-indicators__sections-items"
    >
      <v-tab-item
        v-for="section in visibleSections"
        :key="`item-${section.key}`"
      >
        <div class="general-indicators__section-content">
          <general-section
            v-if="section.key === 'general'"
            :abonents-statistics="abonentsStatistics"
            :loading-abonents-statistics="loadingAbonentsStatistics"
            :can-view-abonents-statistics="canViewAbonentsStatistics"
            :can-view-general-indicators="canViewGeneralIndicators"
            :array-agents="arrayAgents"
            :array-managements="arrayManagements"
            :headers="headers"
            :object-total="objectTotal"
            :display-loading-indicators="displayLoadingIndicators"
            :display-loading="displayLoading"
            :title-display="titleDisplay"
          />
          <mto-sector-indicators-panel
            v-else-if="section.key === 'mto'"
            embedded
            :loading="loadingMtoIndicators"
            :data="mtoIndicatorsData"
            :status-description="mtoStatusDescription"
          />
          <device-offline-daily-chart-panel
            v-else-if="section.key === 'offline'"
            :loading="loadingOfflineStatistics"
            :items="offlineStatisticsItems"
            :filter="offlineStatisticsFilter"
            @change-period="onOfflinePeriodChange"
            @refresh="loadDeviceOfflineStatistics"
          />
        </div>
      </v-tab-item>
    </v-tabs-items>

    <div v-else-if="visibleSections.length === 1" class="general-indicators__section-content">
      <general-section
        v-if="visibleSections[0].key === 'general'"
        :abonents-statistics="abonentsStatistics"
        :loading-abonents-statistics="loadingAbonentsStatistics"
        :can-view-abonents-statistics="canViewAbonentsStatistics"
        :can-view-general-indicators="canViewGeneralIndicators"
        :array-agents="arrayAgents"
        :array-managements="arrayManagements"
        :headers="headers"
        :object-total="objectTotal"
        :display-loading-indicators="displayLoadingIndicators"
        :display-loading="displayLoading"
        :title-display="titleDisplay"
      />
      <mto-sector-indicators-panel
        v-else-if="visibleSections[0].key === 'mto'"
        embedded
        :loading="loadingMtoIndicators"
        :data="mtoIndicatorsData"
        :status-description="mtoStatusDescription"
      />
      <device-offline-daily-chart-panel
        v-else-if="visibleSections[0].key === 'offline'"
        :loading="loadingOfflineStatistics"
        :items="offlineStatisticsItems"
        :filter="offlineStatisticsFilter"
        @change-period="onOfflinePeriodChange"
        @refresh="loadDeviceOfflineStatistics"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ruDateFormatMixin from '@/mixins/ruDateFormat';
import MtoSectorIndicatorsPanel from '@/components/reports/MtoSectorIndicatorsPanel';
import DeviceOfflineDailyChartPanel from '@/components/reports/DeviceOfflineDailyChartPanel';
import GeneralSection from '@/components/reports/GeneralIndicatorsGeneralSection';

export default {
  name: 'BaseGeneralIndicators',
  mixins: [ruDateFormatMixin],
  components: {
    MtoSectorIndicatorsPanel,
    DeviceOfflineDailyChartPanel,
    GeneralSection,
  },
  data: () => ({
    activeSection: 0,
    arrayManagements: [],
    arrayAgents: [],
    headers: [],
    objectTotal: {},
    displayLoading: false,
    displayLoadingIndicators: false,
    tableCellSmall: false,
    abonentsStatistics: {
      abonents_count: 0,
      lite_versions_count: 0,
      pro_versions_count: 0,
    },
    loadingAbonentsStatistics: false,
    loadingMtoIndicators: false,
    mtoIndicatorsData: null,
    mtoStatusDescription: {},
    loadingOfflineStatistics: false,
    offlineStatisticsItems: [],
    offlineStatisticsFilter: { type: 'preset', days: 30 },
    offlineStatisticsLoaded: false,
  }),
  created () {
    window.addEventListener('resize', this.checkSize);
    if (this.canViewGeneralIndicators) {
      this.loadManagementGroup();
      this.getData();
    }
    if (this.canViewAbonentsStatistics) {
      this.loadAbonentsStatistics();
    }
    if (this.canViewMtoIndicators) {
      this.loadMtoSectorIndicators();
    }
    this.checkSize();
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.checkSize);
  },
  watch: {
    activeSection (value) {
      const section = this.visibleSections[value];
      if (section && section.key === 'offline' && !this.offlineStatisticsLoaded) {
        this.loadDeviceOfflineStatistics();
      }
    },
  },
  computed: {
    ...mapGetters({
      managementsGroup: 'GroupManagementCompany/managementsGroup',
      doorphones: 'Doorphone/doorphones',
      roleName: 'roleName',
    }),
    canViewMtoIndicators () {
      return this.$checkPermission('get_mto_sector_indicators');
    },
    canViewAbonentsStatistics () {
      return this.$checkPermission('get_abonents_statistics');
    },
    canViewGeneralIndicators () {
      return this.$checkPermission('get_general_indicators');
    },
    currentDateTime () {
      return this.ruDateFormat(new Date().toISOString(), false, false);
    },
    visibleSections () {
      const sections = [];

      if (this.canViewAbonentsStatistics || this.canViewGeneralIndicators) {
        sections.push({ key: 'general', title: 'Общие показатели' });
      }

      if (this.canViewGeneralIndicators) {
        sections.push({ key: 'offline', title: 'Не в сети' });
      }

      if (this.canViewMtoIndicators) {
        sections.push({ key: 'mto', title: 'Показатели МТО' });
      }

      return sections;
    },
  },
  methods: {
    ...mapActions({
      loadAllAgents: 'Agents/loadAllAgents',
      getDoorphones: 'Doorphone/getDoorphones',
      obtainingIndicatorsDoorphone: 'Reports/obtainingIndicatorsDoorphone',
      getManagementsGroups: 'GroupManagementCompany/getManagementsGroups',
      getAbonentsStatistics: 'Reports/getAbonentsStatistics',
      getMtoSectorIndicators: 'Reports/getMtoSectorIndicators',
      getDeviceOfflineDailyStatistics: 'Reports/getDeviceOfflineDailyStatistics',
    }),
    getData () {
      this.displayLoadingIndicators = true;
      this.obtainingIndicatorsDoorphone()
        .then((response) => {
          this.arrayAgents = response.data.doorphones.agents;
          this.headers = [' ', 'Всего', ...this.arrayAgents.map(agent => agent.name)];
          this.objectTotal = response.data.doorphones.total;
        })
        .catch(() => {
          this.$flash_warning('Ошибка при загрузке информации домофонов по агентам');
        })
        .finally(() => {
          this.displayLoadingIndicators = false;
        });
    },
    loadManagementGroup () {
      this.displayLoading = true;
      this.getManagementsGroups({
      })
        .then((response) => {
          this.arrayManagements = response.data.slice();
          if (!this.tableCellSmall) {
            this.arrayManagements.unshift({ name: '', doorphones_count: 'Кол-во домофонов' });
          } else {
            this.arrayManagements.unshift({ name: '' });
          }
        })
        .catch(() => {
          this.$flash_warning('Ошибка при загрузке групп управляющих компаний');
        })
        .finally(() => {
          this.displayLoading = false;
        });
    },
    encodeCP1251 (string) {
      function encodeChar (c) {
        const isKyr = function (str) {
          return /[а-я]/i.test(str);
        };
        // eslint-disable-next-line no-multi-str
        const cp1251 = 'ЂЃ‚ѓ„…†‡€‰Љ‹ЊЌЋЏђ‘’“”•–—™љ›њќћџ ЎўЈ¤Ґ¦§Ё©Є«¬*®Ї°±Ііґµ¶·\
ё№є»јЅѕїАБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмнопрстуфхцчшщъыьэюя';
        const p = isKyr(c) ? (cp1251.indexOf(c) + 128) : c.charCodeAt(0);
        let h = p.toString(16);
        if (h === 'a') {
          h = '0A';
        }
        return '%' + h;
      }
      let res = '';
      for (let i = 0; i < string.length; i++) {
        res += encodeChar(string.charAt(i));
      }
      return res;
    },
    titleDisplay (key) {
      const keyMap = {
        count: 'Кол-во домофонов',
        st_2: 'Зарегистрирован',
        st_5: 'Не установлен',
        st_6: 'Без интернет соединения',
        st_7: 'Пусконаладка',
        st_8: 'Старый домофон',
        st_9: 'Ожидает монтажа',
        'st_-1': 'Не зарегистрирован',
        st_null: 'Не в сети',
      };
      return keyMap[key] || key;
    },
    downloadReportIndicatorsDoorphone () {
      const lines = [];
      const header = ['', 'Всего'].concat(this.arrayAgents.map(agent => agent.name)).join(';');
      lines.push(header);
      Object.keys(this.objectTotal).forEach(key => {
        const line = [this.titleDisplay(key)].concat(this.objectTotal[key], this.arrayAgents.map(agent => agent.doorphones[key])).join(';');
        lines.push(line);
      });

      const link = document.createElement('a');
      link.href = `data:text/csv;charset=CP1251,${this.encodeCP1251(lines.join('\n'))}`;
      link.download = 'agents-doorphones.csv';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    downloadReportCompanies () {
      const lines = [];
      this.arrayManagements.forEach((item) => {
        const line = [
          item.name, item.doorphones_count,
        ].join(';');
        lines.push(line);
      });
      const link = document.createElement('a');
      link.href = `data:text/csv;charset=CP1251,${this.encodeCP1251(lines.join('\n'))}`;
      link.download = 'companies.csv';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    checkSize () {
      this.tableCellSmall = window.innerWidth <= 700;
    },
    loadAbonentsStatistics () {
      this.loadingAbonentsStatistics = true;
      this.getAbonentsStatistics()
        .then((response) => {
          if (response && response.data) {
            this.abonentsStatistics = {
              abonents_count: response.data.abonents_count || 0,
              lite_versions_count: response.data.lite_versions_count || 0,
              pro_versions_count: response.data.pro_versions_count || 0,
            };
          }
        })
        .catch(() => {
          this.$flash_warning('Ошибка при загрузке статистики абонентов');
        })
        .finally(() => {
          this.loadingAbonentsStatistics = false;
        });
    },
    loadMtoSectorIndicators () {
      this.loadingMtoIndicators = true;
      this.getMtoSectorIndicators()
        .then((response) => {
          const payload = response?.data?.data || response?.data;
          if (payload) {
            this.mtoIndicatorsData = payload;
            this.mtoStatusDescription = payload.status_description || {};
          }
        })
        .catch(() => {
          this.$flash_warning('Ошибка при загрузке показателей МТО');
        })
        .finally(() => {
          this.loadingMtoIndicators = false;
        });
    },
    buildOfflineDateRange (filter) {
      if (filter?.type === 'day' && filter.date) {
        return {
          dateFrom: filter.date,
          dateTo: filter.date,
        };
      }

      const periodDays = filter?.days || 30;
      const dateTo = new Date();
      const dateFrom = new Date();
      dateFrom.setDate(dateTo.getDate() - periodDays);

      return {
        dateFrom: dateFrom.toISOString().slice(0, 10),
        dateTo: dateTo.toISOString().slice(0, 10),
      };
    },
    loadDeviceOfflineStatistics () {
      this.loadingOfflineStatistics = true;
      const { dateFrom, dateTo } = this.buildOfflineDateRange(this.offlineStatisticsFilter);

      return this.getDeviceOfflineDailyStatistics({ dateFrom, dateTo })
        .then((response) => {
          const payload = response?.data || response;
          this.offlineStatisticsItems = payload?.items || [];
          this.offlineStatisticsLoaded = true;
          return this.offlineStatisticsItems;
        })
        .catch(() => {
          this.offlineStatisticsItems = [];
          this.$flash_warning('Ошибка при загрузке статистики устройств «Не в сети»');
        })
        .finally(() => {
          this.loadingOfflineStatistics = false;
        });
    },
    onOfflinePeriodChange (filter) {
      this.offlineStatisticsFilter = filter;
      this.loadDeviceOfflineStatistics();
    },
    buildOfflineDevicesCsv () {
      const lines = ['Дата;Домофоны;Шлагбаумы;Калитки'];
      this.offlineStatisticsItems.forEach((item) => {
        lines.push([
          item.date,
          item.doorphones_offline_count,
          item.barriers_offline_count,
          item.gates_offline_count,
        ].join(';'));
      });
      return lines.join('\n');
    },
    downloadOfflineDevicesReport () {
      const exportData = () => {
        if (!this.offlineStatisticsItems.length) {
          this.$flash_warning('Нет данных для выгрузки за выбранный период');
          return;
        }

        const firstDate = this.offlineStatisticsItems[0]?.date || '';
        const lastDate = this.offlineStatisticsItems[this.offlineStatisticsItems.length - 1]?.date || '';
        const link = document.createElement('a');
        link.href = `data:text/csv;charset=CP1251,${this.encodeCP1251(this.buildOfflineDevicesCsv())}`;
        link.download = `offline-devices_${firstDate}_${lastDate}.csv`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      if (this.offlineStatisticsLoaded && this.offlineStatisticsItems.length) {
        exportData();
        return;
      }

      this.loadDeviceOfflineStatistics()
        .then(() => exportData());
    },
  },
};
</script>

<style lang="scss" scoped>
.general-indicators__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.general-indicators__title {
  font-size: 1.5rem;
  font-weight: 400;
  color: #37474f;
  margin: 0;
  letter-spacing: -0.02em;
}

.general-indicators__btn {
  text-transform: none;
  font-weight: 500;
}

.general-indicators__section-title {
  font-size: 1.25rem;
  font-weight: 400;
  color: #37474f;
  margin: 0 0 12px 0;
  letter-spacing: -0.01em;
}

.general-indicators__sections-wrap {
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  overflow: hidden;
}

.general-indicators__sections {
  box-shadow: none;
}

.general-indicators__sections ::v-deep .v-tabs-bar {
  background: transparent;
  height: 48px;
}

.general-indicators__section-tab {
  text-transform: none;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #546e7a;
  min-height: 48px;
}

.general-indicators__sections ::v-deep .v-tab--active {
  color: #1976d2;
}

.general-indicators__sections ::v-deep .v-tabs-slider {
  color: #1976d2;
  height: 3px;
}

.general-indicators__sections-items {
  background: transparent;
}

.general-indicators__section-content {
  padding-top: 4px;
}
</style>

<style lang="scss">
.custom-table {
  display: inline-block;
  padding: 5px;
  text-align: -webkit-center;
  width: auto;
}

.custom-table th, .custom-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: center;
}

.custom-table th {
  background-color: #f2f2f2;
}
.table-cell {
  display: inline-block;
  border: 0 solid #ccc;
  border-right-width: 1px;
  padding: 5px;
  text-align: -webkit-center;
  width: auto;
}

.table-row {
  display: table-row;
}

.table-cell-header {
  padding-bottom: 10px;
  color: #434850;
}
.table-cell-header:hover {
  color: #000;
  cursor: default;
}
@media screen and (max-width: 1450px) {
  .custom-table {
    display: block;
    width: 100%;
    overflow-x: auto;
  }
}
@media screen and (max-width: 700px) {
  .table-cell {
    display: grid;
    border: 1px solid #ccc;
  }
}
@media screen and (max-width: 600px) {
  .list-item-mobile-version {
    display: inline-grid;
  }
}
.statistics-card {
  height: 100%;
}
.statistics-title {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  text-transform: uppercase;
  font-weight: 500;
}
.statistics-value {
  font-size: 32px;
  font-weight: 600;
  color: #1976d2;
  display: flex;
  align-items: center;
}
.statistics-actual-info {
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
}
.statistics-actual-text {
  margin-right: 8px;
}
.statistics-actual-date {
  font-weight: 500;
  color: #1976d2;
}
.general-indicators__cards {
  margin-top: 0;
}
</style>
